import type { ReactNode } from "react";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Copy,
  Ellipsis,
  EllipsisVertical,
  Hand,
  Lock,
  Menu,
  MonitorDown,
  Plus,
  Share,
  SquarePlus,
  X,
} from "lucide-react";
import { useT } from "@ec2/i18n/react";
import type { SceneId } from "../../lib/install-guides";
import type { InstallPlatform } from "../../lib/platform";

/**
 * Stylised browser chrome for the install walkthrough: a phone or a window,
 * the bit of browser UI the current step is about, and a pulsing tap marker on
 * the element to touch. Purely illustrative — `aria-hidden`; the step captions
 * carry the information (Walkthrough.tsx).
 */
export function MockDevice({ scene, platform }: { scene: SceneId; platform: InstallPlatform }) {
  const desktop = scene.startsWith("desktop-") || scene.startsWith("mac-");
  return (
    <div
      aria-hidden
      className="flex justify-center"
      data-testid="ec-install-mock"
      data-scene={scene}
    >
      {desktop ? (
        <DesktopFrame mac={scene.startsWith("mac-")}>{renderScene(scene, platform)}</DesktopFrame>
      ) : (
        <PhoneFrame>{renderScene(scene, platform)}</PhoneFrame>
      )}
    </div>
  );
}

function renderScene(scene: SceneId, platform: InstallPlatform): ReactNode {
  switch (scene) {
    case "ios-bottom-share":
      return <IosSafari />;
    case "ios-address-share":
      return <IosAddressShare />;
    case "ios-share-sheet":
      return <IosShareSheet />;
    case "ios-add-confirm":
      return <IosAddConfirm />;
    case "home-screen":
      return <HomeScreen />;
    case "inapp-menu":
      return <InAppBrowser />;
    case "inapp-open-browser":
      return <InAppMenu ios={platform.startsWith("ios")} />;
    case "android-menu":
      return <AndroidChrome />;
    case "android-menu-install":
      return <AndroidMenu />;
    case "android-install-dialog":
      return <AndroidInstallDialog />;
    case "android-install-choice":
      return <AndroidInstallChoice />;
    case "firefox-menu":
      return <FirefoxBar />;
    case "firefox-menu-add":
      return <FirefoxMenu />;
    case "firefox-add-confirm":
      return <FirefoxAddConfirm />;
    case "samsung-menu":
      return <Samsung />;
    case "samsung-add-page":
      return <SamsungAddPage />;
    case "desktop-omnibox-install":
      return <DesktopOmnibox />;
    case "desktop-install-dialog":
      return <DesktopInstallDialog />;
    case "mac-file-menu":
      return <MacFileMenu />;
    case "mac-add-dock":
      return <MacAddDock />;
  }
}

/** The address the player actually sees in their bar — names, not copy to translate. */
const HOST = typeof window !== "undefined" ? window.location.host : "eventconnect.cloud";
const SAFARI = "Safari";

// ------------------------------------------------------------------ building blocks

/** The element to touch: highlighted, with a pulsing marker and a finger. */
function Tap({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`relative inline-flex items-center justify-center rounded-lg ring-2 ring-accent ${className}`}
    >
      {children}
      <span className="pointer-events-none absolute inset-0 animate-ping rounded-lg bg-accent/30 motion-reduce:animate-none" />
      <Hand
        size={20}
        className="pointer-events-none absolute -bottom-4 -right-3 rotate-[-20deg] fill-surface text-fg drop-shadow motion-safe:animate-bounce"
      />
    </span>
  );
}

function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <div className="relative h-[360px] w-[200px] overflow-hidden rounded-[28px] border-[6px] border-fg/80 bg-surface shadow-lg">
      <div className="absolute left-1/2 top-1.5 z-20 h-3 w-14 -translate-x-1/2 rounded-full bg-fg/80" />
      {children}
    </div>
  );
}

function DesktopFrame({ children, mac }: { children: ReactNode; mac: boolean }) {
  return (
    <div className="relative h-[210px] w-[300px] overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
      {!mac && (
        <div className="flex h-6 items-center gap-1.5 bg-surface-raised px-2">
          <span className="h-2 w-2 rounded-full bg-fg/30" />
          <span className="h-2 w-2 rounded-full bg-fg/30" />
          <span className="h-2 w-2 rounded-full bg-fg/30" />
          <span className="ml-2 h-4 w-24 rounded-t bg-surface" />
        </div>
      )}
      {children}
    </div>
  );
}

/** The game's own page, greyed, behind the browser UI. */
function FakePage({ dim = false }: { dim?: boolean }) {
  return (
    <div className={`flex flex-col gap-2 p-3 ${dim ? "opacity-40" : ""}`}>
      <div className="h-7 rounded-lg bg-gradient-to-r from-accent to-cat-photo" />
      <div className="h-12 rounded-xl bg-fg/8" />
      <div className="h-12 rounded-xl bg-fg/8" />
      <div className="h-12 rounded-xl bg-fg/8" />
    </div>
  );
}

function Url({ children }: { children?: ReactNode }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1 truncate rounded-full bg-surface-raised px-2 py-1 text-[9px] text-fg-secondary">
      <Lock size={9} /> {HOST}
      {children}
    </span>
  );
}

function Sheet({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-x-0 bottom-0 z-10 rounded-t-2xl border-t border-border bg-surface p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.15)]">
      {children}
    </div>
  );
}

function Row({ icon, label, tap }: { icon: ReactNode; label: string; tap?: boolean }) {
  const content = (
    <span className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-[11px] text-fg">
      {label}
      {icon}
    </span>
  );
  return <div className="w-full">{tap ? <Tap className="w-full">{content}</Tap> : content}</div>;
}

function Dialog({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-fg/30 p-4">
      <div className="w-full rounded-2xl bg-surface p-3 text-center shadow-xl">{children}</div>
    </div>
  );
}

function AppIcon({ size = 40 }: { size?: number }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-[22%] bg-gradient-to-br from-accent to-cat-photo font-display font-bold text-white"
      style={{ width: size, height: size, fontSize: size * 0.45 }}
    >
      E
    </span>
  );
}

// ------------------------------------------------------------------ scenes

function IosSafari() {
  return (
    <>
      <div className="pt-6">
        <FakePage />
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-border bg-surface-raised px-2 pb-4 pt-2 text-fg-secondary">
        <ChevronLeft size={16} />
        <ChevronRight size={16} />
        <Tap className="p-1">
          <Share size={16} className="text-accent" />
        </Tap>
        <BookOpen size={16} />
        <SquarePlus size={16} />
      </div>
    </>
  );
}

function IosAddressShare() {
  return (
    <>
      <div className="flex items-center gap-1 bg-surface-raised px-2 pb-2 pt-6">
        <Url />
        <Tap className="p-1">
          <Share size={14} className="text-accent" />
        </Tap>
      </div>
      <FakePage />
    </>
  );
}

function IosShareSheet() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage dim />
      </div>
      <Sheet>
        <div className="mb-2 flex justify-around">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="h-7 w-7 rounded-lg bg-surface-raised" />
          ))}
        </div>
        <div className="flex flex-col divide-y divide-border rounded-xl bg-surface-raised">
          <Row icon={<Copy size={12} />} label={t("ec2.install.ui.copy")} />
          <Row icon={<SquarePlus size={12} />} label={t("ec2.install.ui.addToHome")} tap />
        </div>
      </Sheet>
    </>
  );
}

function IosAddConfirm() {
  const t = useT();
  return (
    <div className="flex flex-col gap-3 bg-surface-raised px-3 pb-3 pt-7">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-accent">{t("ec2.install.ui.cancel")}</span>
        <span className="min-w-0 truncate whitespace-nowrap px-1 text-[9px] font-semibold text-fg">
          {t("ec2.install.ui.addToHome")}
        </span>
        <Tap className="px-1.5 py-0.5">
          <span className="font-semibold text-accent">{t("ec2.install.ui.add")}</span>
        </Tap>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-surface p-2">
        <AppIcon size={34} />
        <span className="text-[11px] text-fg">{t("ec2.install.ui.appName")}</span>
      </div>
    </div>
  );
}

function HomeScreen() {
  return (
    <div className="grid h-full grid-cols-4 content-start gap-3 bg-cat-install/25 px-3 pb-3 pt-9">
      {Array.from({ length: 11 }, (_, i) => (
        <span key={i} className="mx-auto h-9 w-9 rounded-[22%] bg-surface/80" />
      ))}
      <span className="mx-auto">
        <Tap className="rounded-[22%]">
          <AppIcon size={36} />
        </Tap>
      </span>
    </div>
  );
}

function InAppBrowser() {
  return (
    <>
      <div className="flex items-center gap-2 bg-surface-raised px-2 pb-2 pt-6 text-fg-secondary">
        <X size={14} />
        <span className="flex-1 truncate text-center text-[9px]">{HOST}</span>
        <Tap className="p-1">
          <Ellipsis size={14} className="text-accent" />
        </Tap>
      </div>
      <FakePage />
    </>
  );
}

function InAppMenu({ ios }: { ios: boolean }) {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage dim />
      </div>
      <Sheet>
        <div className="flex flex-col divide-y divide-border rounded-xl bg-surface-raised">
          <Row icon={<Copy size={12} />} label={t("ec2.install.ui.copy")} />
          <Row
            icon={<Share size={12} />}
            label={t(ios ? "ec2.install.ui.openInSafari" : "ec2.install.ui.openInBrowser")}
            tap
          />
        </div>
      </Sheet>
    </>
  );
}

function AndroidChrome() {
  return (
    <>
      <div className="flex items-center gap-1 bg-surface-raised px-2 pb-2 pt-6 text-fg-secondary">
        <Url />
        <Tap className="p-1">
          <EllipsisVertical size={14} className="text-accent" />
        </Tap>
      </div>
      <FakePage />
    </>
  );
}

function AndroidMenu() {
  const t = useT();
  return (
    <>
      <div className="flex items-center gap-1 bg-surface-raised px-2 pb-2 pt-6">
        <Url />
        <EllipsisVertical size={14} className="text-fg-secondary" />
      </div>
      <FakePage dim />
      <div className="absolute right-2 top-12 z-10 w-36 rounded-xl border border-border bg-surface py-1 shadow-xl">
        <Row icon={<Plus size={12} />} label={t("ec2.install.ui.newTab")} />
        <Row icon={<BookOpen size={12} />} label={t("ec2.install.ui.bookmarks")} />
        <Row icon={<MonitorDown size={12} />} label={t("ec2.install.ui.addToHomeScreen")} tap />
      </div>
    </>
  );
}

function AndroidInstallDialog() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage />
      </div>
      <Dialog>
        <div className="mb-2 flex justify-center">
          <AppIcon size={36} />
        </div>
        <p className="mb-3 text-[11px] font-semibold text-fg">
          {t("ec2.install.ui.installQuestion")}
        </p>
        <div className="flex justify-end gap-3 text-[10px]">
          <span className="text-fg-secondary">{t("ec2.install.ui.cancel")}</span>
          <Tap className="px-1.5 py-0.5">
            <span className="font-semibold text-accent">{t("ec2.install.ui.install")}</span>
          </Tap>
        </div>
      </Dialog>
    </>
  );
}

/** Chrome's "Add to Home screen" dialog: install the app, or only a shortcut. */
function AndroidInstallChoice() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage />
      </div>
      <Dialog>
        <div className="mb-2 flex justify-center">
          <AppIcon size={32} />
        </div>
        <p className="mb-2 text-[11px] font-semibold text-fg">{t("ec2.install.ui.addToHomeScreen")}</p>
        <div className="flex flex-col gap-1 text-left">
          <Row icon={<MonitorDown size={12} />} label={t("ec2.install.ui.install")} tap />
          <Row icon={<SquarePlus size={12} />} label={t("ec2.install.ui.createShortcut")} />
        </div>
      </Dialog>
    </>
  );
}

/** Firefox for Android: address bar and ⋮ menu at the bottom (the default). */
function FirefoxToolbar({ tapMenu }: { tapMenu: boolean }) {
  return (
    <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 border-t border-border bg-surface-raised px-2 pb-4 pt-2 text-fg-secondary">
      <Url />
      {tapMenu ? (
        <Tap className="p-1">
          <EllipsisVertical size={14} className="text-accent" />
        </Tap>
      ) : (
        <EllipsisVertical size={14} />
      )}
    </div>
  );
}

function FirefoxBar() {
  return (
    <>
      <div className="pt-6">
        <FakePage />
      </div>
      <FirefoxToolbar tapMenu />
    </>
  );
}

function FirefoxMenu() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage dim />
      </div>
      <div className="absolute inset-x-2 bottom-14 z-10 rounded-xl border border-border bg-surface py-1 shadow-xl">
        <Row icon={<Plus size={12} />} label={t("ec2.install.ui.newTab")} />
        <Row icon={<BookOpen size={12} />} label={t("ec2.install.ui.bookmarks")} />
        <Row icon={<SquarePlus size={12} />} label={t("ec2.install.ui.firefoxAddApp")} tap />
      </div>
      <FirefoxToolbar tapMenu={false} />
    </>
  );
}

function FirefoxAddConfirm() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage />
      </div>
      <Dialog>
        <div className="mb-2 flex justify-center">
          <AppIcon size={34} />
        </div>
        <p className="mb-3 text-[11px] text-fg">{t("ec2.install.ui.appName")}</p>
        <div className="flex justify-end gap-3 text-[10px]">
          <span className="text-fg-secondary">{t("ec2.install.ui.cancel")}</span>
          <Tap className="px-1.5 py-0.5">
            <span className="font-semibold text-accent">{t("ec2.install.ui.add")}</span>
          </Tap>
        </div>
      </Dialog>
    </>
  );
}

function Samsung() {
  return (
    <>
      <div className="flex items-center bg-surface-raised px-2 pb-2 pt-6">
        <Url />
      </div>
      <FakePage />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-border bg-surface-raised px-2 pb-4 pt-2 text-fg-secondary">
        <ChevronLeft size={16} />
        <ChevronRight size={16} />
        <SquarePlus size={16} />
        <Tap className="p-1">
          <Menu size={16} className="text-accent" />
        </Tap>
      </div>
    </>
  );
}

function SamsungAddPage() {
  const t = useT();
  return (
    <>
      <div className="pt-6">
        <FakePage dim />
      </div>
      <Sheet>
        <div className="flex flex-col divide-y divide-border rounded-xl bg-surface-raised">
          <Row icon={<ChevronRight size={12} />} label={t("ec2.install.ui.addPageTo")} />
          <div className="pl-4">
            <Row icon={<SquarePlus size={12} />} label={t("ec2.install.ui.homeScreen")} tap />
          </div>
        </div>
      </Sheet>
    </>
  );
}

function DesktopOmnibox() {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5 text-fg-secondary">
        <ChevronLeft size={12} />
        <ChevronRight size={12} />
        <Url>
          <span className="ml-auto">
            <Tap className="p-0.5">
              <MonitorDown size={11} className="text-accent" />
            </Tap>
          </span>
        </Url>
      </div>
      <FakePage />
    </>
  );
}

function DesktopInstallDialog() {
  const t = useT();
  return (
    <>
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
        <Url />
      </div>
      <FakePage dim />
      <div className="absolute right-3 top-14 z-10 w-44 rounded-xl border border-border bg-surface p-3 shadow-xl">
        <div className="mb-2 flex items-center gap-2">
          <AppIcon size={24} />
          <span className="text-[11px] font-semibold text-fg">
            {t("ec2.install.ui.installQuestion")}
          </span>
        </div>
        <div className="flex justify-end gap-2 text-[10px]">
          <span className="text-fg-secondary">{t("ec2.install.ui.cancel")}</span>
          <Tap className="px-1.5 py-0.5">
            <span className="font-semibold text-accent">{t("ec2.install.ui.install")}</span>
          </Tap>
        </div>
      </div>
    </>
  );
}

function MacFileMenu() {
  const t = useT();
  return (
    <>
      <div className="flex items-center gap-3 bg-surface-raised px-3 py-1 text-[10px] text-fg">
        <span className="font-semibold">{SAFARI}</span>
        <span className="rounded bg-accent/15 px-1">{t("ec2.install.ui.file")}</span>
      </div>
      <FakePage dim />
      <div className="absolute left-12 top-6 z-10 w-40 rounded-lg border border-border bg-surface py-1 shadow-xl">
        <Row icon={<Plus size={11} />} label={t("ec2.install.ui.newTab")} />
        <Row icon={<SquarePlus size={11} />} label={t("ec2.install.ui.addToDock")} tap />
      </div>
    </>
  );
}

function MacAddDock() {
  const t = useT();
  return (
    <>
      <div className="h-5 bg-surface-raised" />
      <FakePage />
      <Dialog>
        <div className="mb-2 flex justify-center">
          <AppIcon size={34} />
        </div>
        <p className="mb-3 text-[11px] text-fg">{t("ec2.install.ui.appName")}</p>
        <div className="flex justify-end gap-3 text-[10px]">
          <span className="text-fg-secondary">{t("ec2.install.ui.cancel")}</span>
          <Tap className="px-1.5 py-0.5">
            <span className="font-semibold text-accent">{t("ec2.install.ui.add")}</span>
          </Tap>
        </div>
      </Dialog>
    </>
  );
}
